import type {NavigateFunction} from 'react-router';

import NiceModal from '@ebay/nice-modal-react';

import type {ConferenceApiModel} from '@yourcoach/shared/api/conference';
import type {PaymentContext} from '@yourcoach/shared/api/event';
import type {ClientTaskExpandedCommon} from '@yourcoach/shared/api/task';
import type {Tab} from '@yourcoach/shared/modules/course/stores/TabsManager/types';
import type {NotificationsListItem} from '@yourcoach/shared/modules/notifications/components/NotificationsListItem';
import type {NotificationsStore} from '@yourcoach/shared/modules/notifications/stores/Notifications';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import {i18n} from '@yourcoach/i18n';
import {complaintStore} from '@yourcoach/shared/api/complaint';
import {membershipStore} from '@yourcoach/shared/api/membership';
import {alert} from '@yourcoach/shared/components/alert';
import {showEditionNewDetailsAlert} from '@yourcoach/shared/modules/program';
import {inviteDelete} from '@yourcoach/shared/services/entity-action/invite/invite.delete';
import {isMeCoachForEntity} from '@yourcoach/shared/utils/entity/isMeCoachForEntity';
import {logger} from '@yourcoach/shared/utils/logger';

import {routerPath} from '@src/routes/path';
import {CromSolvingModal} from '@src/v2/modules/crom-solving-modal';
import {PaymentModal} from '@src/v2/modules/payment-modal';
import {RequestRefundModal} from '@src/v2/modules/request-refund';
import {joinConference} from '@src/v2/services/entity-action/conference/conference.join';

type OnClickArgs = Parameters<
  GetComponentProps<typeof NotificationsListItem>['onClick']
>;

export const getClickHandler = ({
  contexts,
  event,
  id,
  navigate,
  store,
}: {
  contexts: OnClickArgs[2];
  event: OnClickArgs[1];
  id: OnClickArgs[0];
  navigate: NavigateFunction;
  store: InstanceType<typeof NotificationsStore>;
}) => {
  const {
    actor,
    bound_form,
    certificate,
    complaint,
    conference,
    course,
    goal,
    invite,
    membership,
    payment,
    post,
    program,
    subgoal,
    task,
    user_questionnaire,
  } = contexts;

  switch (id) {
    case 'availability_needs_to_be_updated_3days_before':
    case 'availability_needs_to_be_updated_now':
    case 'availability_needs_to_be_updated_week_before':
    case 'coach_schedule_until_expired':
    case 'google_oauth_access_became_invalid': {
      return () =>
        navigate(
          routerPath.availability.$buildPath({
            searchParams: {
              os: '1',
            },
          }),
        );
    }
    case 'bound_form_missed': {
      if (bound_form?._id) {
        return () =>
          NiceModal.show(CromSolvingModal, {
            cromId: bound_form._id,
            placeForLogging: 'notification',
          });
      }

      return undefined;
    }
    case 'certificate_approved':
    case 'certificate_rejected': {
      if (certificate?._id) {
        return undefined;
      }

      return undefined;
    }
    case 'coach_matched':
    case 'course_extended':
    case 'course_is_already_ended':
    case 'course_paused':
    case 'course_resumed':
    case 'course_started':
    case 'course_stopped':
    case 'course_stopped_restarting':
    case 'end_of_program_reminder':
    case 'membership_accepted':
    case 'membership_created': {
      if (course?._id && course.program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.course.$buildPath({
            params: {
              courseId: course._id,
              practiceSlugOrId:
                course.program.user?.slug || course.program.user_id!,
              programSlugOrId: course.program.slug || course.program._id!,
            },
            searchParams: {
              pid: course.program._id!,
              sqid: course._id,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'complaint_received': {
      if (post?._id) {
        return () => {
          const path = routerPath.chats.$buildPath({
            searchParams: {
              cid: post.channel_id,
            },
          });
          const state = routerPath.chats.$buildState({
            state: {
              postId: post._id,
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'conference_archived':
    case 'conference_archives_shared':
    case 'conference_client_joined':
    case 'conference_pending':
    case 'conference_rescheduled':
    case 'conference_scheduled':
    case 'conference_still_active':
    case 'conference_stopped_automatically':
    case 'conference_taken':
    case 'conference_upcoming':
    case 'conference_upcoming_5min':
    case 'user_canceled_session': {
      if (conference?._id) {
        const path = routerPath.event.$buildPath({
          params: {
            eventId: conference._id!,
          },
        });

        return () => navigate(path);
      }

      return undefined;
    }
    case 'contact_client_button':
    case 'contact_coach_button':
    case 'program_shared_message_button': {
      if (actor?._id) {
        return () => {
          const path = routerPath.chats.$buildPath({});

          const state = routerPath.chats.$buildState({
            state: {
              userIds: [actor._id!],
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'course_created':
    case 'course_pending': {
      if (program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'decline_invite_button': {
      logger.event('invite_decline_tap', {place: 'notification'});

      if (membership?._id) {
        return () => {
          inviteDelete({invite: membership});
        };
      }

      return undefined;
    }
    case 'edition_created':
    case 'edition_signed':
    case 'program_rejected':
    case 'program_resigned':
    case 'program_shared':
    case 'program_signed':
    case 'program_updated': {
      if (program?._id) {
        if (isMeCoachForEntity(program)) {
          return () => {
            const path = routerPath.coachPractice.program.$buildPath({
              params: {
                practiceSlugOrId: program.user?.slug || program.user_id!,
                programSlugOrId: program.slug || program._id!,
              },
            });

            navigate(path);
          };
        }
      }

      return undefined;
    }
    case 'goal_client_review_expiring':
    case 'goal_client_review_reminder': {
      if (goal?._id) {
        return () => {
          const path = routerPath.goals.$buildPath({
            searchParams: {
              goalId: goal._id,
            },
          });
          const state = routerPath.goals.$buildState({
            state: {
              courseId: goal.course_id ?? undefined,
              RateGoals: true,
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'goal_coach_review_expiring':
    case 'goal_coach_review_reminder': {
      if (goal?._id && program?._id && goal.course_id) {
        return () => {
          const path = routerPath.coachPractice.program.course.goals.$buildPath(
            {
              params: {
                courseId: goal.course_id!,
                practiceSlugOrId: program.user?.slug || program.user_id!,
                programSlugOrId: program.slug || program._id!,
              },
              searchParams: {
                pid: program._id!,
                sqid: goal.course_id!,
              },
            },
          );

          navigate(path);
        };
      }

      return undefined;
    }
    case 'goal_reached': {
      if (goal?.parent_goal_id) {
        return () => {
          const path = routerPath.goals.$buildPath({
            searchParams: {
              goalId: goal.parent_goal_id!,
            },
          });
          const state = routerPath.goals.$buildState({
            state: {
              forCoach: true,
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'invite_learn_more_button': {
      logger.event('invite_more_tap', {place: 'notification'});

      if (program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'invite_received': {
      if (!invite) {
        return undefined;
      }

      if (program && program._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      if (invite.user_questionnaire_id && invite.user_questionnaire?._id) {
        if (
          invite.user_questionnaire.answered ||
          invite.user_questionnaire.last_answer
        ) {
          return () => {
            navigate(
              routerPath.userQuestionnaire.progress.$buildPath({
                searchParams: {
                  id: invite.user_questionnaire_id!,
                },
              }),
            );
          };
        }

        return () => {
          navigate(
            routerPath.userQuestionnaire.about.$buildPath({
              searchParams: {
                id: invite.user_questionnaire_id!,
              },
            }),
          );
        };
      }

      if (actor?._id) {
        return () => {
          const path = routerPath.coachPractice.$buildPath({
            params: {
              practiceSlugOrId: actor.slug || actor._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'join_conference_button': {
      if (conference?._id) {
        return async () => {
          const foundMembership = membershipStore.membershipLookup.get(
            conference.course_id || '',
          );

          const userIsFrozen = foundMembership?.status === 'frozen';

          if (userIsFrozen) {
            if (course?._id && program?._id) {
              const path = routerPath.coachPractice.program.course.$buildPath({
                params: {
                  courseId: course._id!,
                  practiceSlugOrId: program.user?.slug || program.user_id!,
                  programSlugOrId: program.slug || program._id!,
                },
                searchParams: {
                  pid: program._id!,
                  sqid: course._id!,
                },
              });

              navigate(path);
            }
          } else {
            joinConference(
              {
                ...conference,
                // @ts-expect-error we need the course to redirect to channel in case of chat sessions
                course: course || undefined,
              } as ConferenceApiModel,
              navigate,
            );
          }
        };
      }

      return undefined;
    }
    case 'materials_disclosed': {
      if (course?._id) {
        return () => {
          const path = routerPath.chats.$buildPath({
            searchParams: {
              cid: course.channel_ids[0],
            },
          });
          const state = routerPath.chats.$buildState({
            state: {
              tab: 'library',
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'membership_declined':
    case 'membership_declined_by_client':
    case 'membership_expired': {
      if (program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'membership_frozen':
    case 'membership_unfrozen': {
      if (course?._id && program?._id) {
        let tab: Tab['key'] | undefined;

        if (membership && membership.frozen_by === 'failed_payment') {
          tab = 'payments';
        }

        return () => {
          const path = routerPath.coachPractice.program.course.$buildPath({
            params: {
              courseId: course._id!,
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
            searchParams: {
              pid: program._id!,
              sqid: course._id!,
            },
          });

          const state = routerPath.coachPractice.program.course.$buildState({
            state: {
              tab,
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'membership_proposed':
    case 'membership_revoked': {
      if (program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'membership_requested': {
      if (program && program._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });
          const state = routerPath.coachPractice.program.$buildState({
            state: {
              tab: 'requests',
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'offered_program_created_client': {
      if (membership?._id) {
        if (
          membership.status === 'accepted' &&
          membership.course?.channel_ids.length
        ) {
          return () => {
            const path = routerPath.chats.$buildPath({
              searchParams: {
                cid: membership.course?.channel_ids[0],
              },
            });

            navigate(path);
          };
        } else if (membership.status === 'proposed' && program?._id) {
          return () => {
            const path = routerPath.coachPractice.program.$buildPath({
              params: {
                practiceSlugOrId: program.user?.slug || program.user_id!,
                programSlugOrId: program.slug || program._id!,
              },
            });

            navigate(path);
          };
        }
      }

      return undefined;
    }
    case 'offered_program_created_coach': {
      if (program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.$buildPath({
            params: {
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    case 'payment_failed':
    case 'payment_marked_paid':
    case 'payment_requires_action':
    case 'payment_upcoming': {
      const isPaymentRequiredAttention =
        event.type === 'payment_failed' ||
        event.type === 'payment_requires_action';
      const isRefundPayment = payment?.type === 'refund';

      const needShowOriginalPayment =
        isRefundPayment && isPaymentRequiredAttention;

      if (payment?._id && needShowOriginalPayment) {
        return () => {
          NiceModal.show(PaymentModal, {paymentId: getPaymentId(payment)});
        };
      }

      if (course?._id && program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.course.$buildPath({
            params: {
              courseId: course._id!,
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
            searchParams: {
              pid: program._id!,
              sqid: course._id!,
            },
          });

          const state = routerPath.coachPractice.program.course.$buildState({
            state: {
              tab: 'payments',
            },
          });

          navigate(path, {state});
        };
      }

      if (payment?.type === 'subscription') {
        return () => {
          navigate(routerPath.profile.subscription.$buildPath({}));
        };
      }

      return undefined;
    }
    case 'payment_refund_failed':
    case 'payment_refunded':
    case 'payment_reversed':
    case 'payment_successful':
    case 'payment_transferred': {
      const isSubscriptionPayment = payment?.type === 'subscription';

      if (payment?._id && !isSubscriptionPayment) {
        return () => {
          NiceModal.show(PaymentModal, {paymentId: getPaymentId(payment)});
        };
      }

      return undefined;
    }
    case 'questionnaire_completed': {
      if (user_questionnaire?._id) {
        return () =>
          navigate(
            routerPath.questionnaire.view.status.$buildPath({
              searchParams: {
                id: user_questionnaire._id!,
              },
            }),
          );
      }

      return undefined;
    }
    case 'refund_button': {
      if (course?._id && program?._id) {
        return () => {
          const path = routerPath.coachPractice.program.course.$buildPath({
            params: {
              courseId: course._id!,
              practiceSlugOrId: program.user?.slug || program.user_id!,
              programSlugOrId: program.slug || program._id!,
            },
            searchParams: {
              pid: program._id!,
              sqid: course._id!,
            },
          });

          const state = routerPath.coachPractice.program.course.$buildState({
            state: {
              tab: 'payments',
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'request_refund_button': {
      if (course?._id) {
        return () => NiceModal.show(RequestRefundModal, {courseId: course._id});
      }

      return undefined;
    }
    case 'resolve_complaint_button': {
      if (complaint?._id) {
        return async () => {
          try {
            const complaintIndex = event.contexts.findIndex(
              context => context && context._id.split(':')[0] === 'complaint',
            );

            if (complaintIndex < 0) {
              throw new Error('Complaint not found');
            }

            const resolvedComplaint = await complaintStore.resolve(complaint);

            event.contexts.splice(complaintIndex, 1, resolvedComplaint);

            store.todayEventsStore.updateItem(event, event);
            store.earlierEventsStore.updateItem(event, event);
          } catch (error) {
            logger.error(error);

            alert.show({
              message: error.message,
              title: i18n.t('shared:message.error'),
            });
          }
        };
      }

      return undefined;
    }
    case 'show_program_changes_button': {
      const programLastEdition = program?.editions?.slice().pop();
      const courseEdition = course?.edition;
      const edition = courseEdition || programLastEdition;

      if (edition) {
        showEditionNewDetailsAlert({
          edition,
        });
      }

      return undefined;
    }
    case 'start_conference_button': {
      if (conference?._id) {
        return () => {
          joinConference(
            {
              ...conference,
              // @ts-expect-error: we need the course to redirect to channel in case of chat sessions
              course: course || undefined,
            } as ConferenceApiModel,
            navigate,
          );
        };
      }

      return undefined;
    }
    case 'subgoal_reached': {
      if (goal?._id && subgoal?._id) {
        return () => {
          const path = routerPath.goals.$buildPath({
            searchParams: {
              goalId: goal.parent_goal_id!,
            },
          });

          const state = routerPath.goals.$buildState({
            state: {
              ClientGoal: true,
              clientId: subgoal.client_id ?? undefined,
              subgoalId: subgoal.parent_subgoal_id ?? undefined,
            },
          });

          navigate(path, {state});
        };
      }

      return undefined;
    }
    case 'task_done': {
      if (course && task?._id) {
        return () => {
          const forCoach = isMeCoachForEntity(course);

          if (forCoach) {
            const path = routerPath.event.$buildPath({
              params: {
                eventId: (task as ClientTaskExpandedCommon).coach_task_id!,
              },
            });

            navigate(path);
          } else {
            const path = routerPath.event.$buildPath({
              params: {
                eventId: task._id!,
              },
            });

            navigate(path);
          }
        };
      }

      return undefined;
    }
    case 'task_overdue':
    case 'task_pending': {
      if (task?._id) {
        return () => {
          const path = routerPath.event.$buildPath({
            params: {
              eventId: task._id!,
            },
          });

          navigate(path);
        };
      }

      return undefined;
    }
    default:
      return undefined;
  }
};

const getPaymentId = (payment: NonNullable<PaymentContext>): string => {
  let paymentId = payment._id;

  if (payment.original_payments.filter(Boolean).length) {
    paymentId = payment.original_payments[0]!._id;
  }

  return paymentId;
};
